import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { FaBars, FaPlus, FaSearch, FaBell, FaTimes, FaUsers, FaPlay, FaPause } from 'react-icons/fa';
import { GoHome, GoHomeFill } from "react-icons/go";
import { TbCategory, TbCategoryFilled } from "react-icons/tb";
import { LuCircleFadingArrowUp } from "react-icons/lu";
import { MdOutlinePayments } from "react-icons/md";
import { LiaHandsHelpingSolid } from "react-icons/lia";
import { SiBillboard } from "react-icons/si";
import { GrCircleInformation } from "react-icons/gr";
import { IoMdHelpCircleOutline } from "react-icons/io";
import { BsArrowUpCircleFill } from "react-icons/bs";
import { AiOutlineMessage, AiFillMessage } from "react-icons/ai";
import { toast } from 'react-toastify';
import { db } from '../firebase';
import { collection, query, where, getDocs, getDoc, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import Logo from '../assets/svg/images/aweitflix logo white.png';
import MobileLogo from '../assets/svg/images/AweitFlix Logo.png';
import defaultAvatar from '../assets/svg/images/default-avatar.jpg';

// CSS for AI button styling
const aiButtonStyles = `
  .ai-button {
    position: relative;
    width: 48px;
    height: 48px;
    background: linear-gradient(45deg, #00f7ff, #ff00ff, #00ffaa);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 15px rgba(0, 255, 255, 0.5);
    transition: transform 0.2s ease, box-shadow 0.3s ease;
    animation: glowPulse 2s infinite ease-in-out;
  }

  .ai-button:hover {
    transform: scale(1.1);
    box-shadow: 0 0 25px rgba(0, 255, 255, 0.8);
  }

  .ai-button::before {
    content: 'AweitAI';
    position: absolute;
    top: -20px;
    font-size: 12px;
    font-weight: bold;
    color: #00f7ff;
    text-shadow: 0 0 5px rgba(0, 255, 255, 0.8);
  }

  .ai-button-icon {
    color: #fff;
    font-size: 20px;
    filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.5));
  }

  @keyframes glowPulse {
    0% {
      box-shadow: 0 0 15px rgba(0, 255, 255, 0.5), 0 0 30px rgba(255, 0, 255, 0.3);
    }
    50% {
      box-shadow: 0 0 25px rgba(0, 255, 255, 0.8), 0 0 40px rgba(255, 0, 255, 0.5);
    }
    100% {
      box-shadow: 0 0 15px rgba(0, 255, 255, 0.5), 0 0 30px rgba(255, 0, 255, 0.3);
    }
  }
`;

// Inject styles
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = aiButtonStyles;
document.head.appendChild(styleSheet);

export default function Header({ isVisible }) {
    const [pageState, setPageState] = useState("Sign In");
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [userCategory, setUserCategory] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [profileImage, setProfileImage] = useState(defaultAvatar);
    const [notificationCount, setNotificationCount] = useState(0);
    const [welcomeAudioUrl, setWelcomeAudioUrl] = useState(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const audioRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const auth = getAuth();
    const menuRef = useRef(null);
    const menuButtonRef = useRef(null);

    // Fetch user data and auth state
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setPageState('Profile');
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setUserCategory(userData.userCategory);
                    setProfileImage(userData.photoURL || defaultAvatar);
                }
            } else {
                setPageState("Sign In");
                setUserCategory(null);
                setProfileImage(defaultAvatar);
                setNotificationCount(0);
            }
        });
        return unsubscribe;
    }, [auth]);

    // Fetch unread notification count
    useEffect(() => {
        if (!auth.currentUser) {
            setNotificationCount(0);
            return;
        }

        const q = query(
            collection(db, `notifications/${auth.currentUser.uid}/userNotifications`),
            where('isRead', '==', false)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setNotificationCount(snapshot.size);
        }, (error) => {
            console.error('Error fetching notification count:', error);
            setNotificationCount(0);
        });

        return () => unsubscribe();
    }, [auth.currentUser]);

    // Fetch the welcome audio
    useEffect(() => {
        async function fetchWelcomeAudio() {
            try {
                const musicQuery = query(
                    collection(db, 'music'),
                    where('title', '==', 'AweitFlix AI Speech')
                );
                const musicSnapshot = await getDocs(musicQuery);
                if (musicSnapshot.empty) {
                    console.error('Renah is away!');
                    const allMusic = await getDocs(collection(db, 'music'));
                    allMusic.forEach((doc) => {
                        console.log('Available music document:', doc.id, doc.data());
                    });
                    toast.error('Aweit AI in Off-Mode!');
                    return;
                }

                const welcomeDoc = musicSnapshot.docs[0];
                const audioUrl = welcomeDoc.data().url;
                console.log('Fetched welcome audio URL:', audioUrl);
                setWelcomeAudioUrl(audioUrl);
            } catch (error) {
                console.error('Error fetching welcome audio:', error);
                toast.error('Failed to load welcome message');
            }
        }

        fetchWelcomeAudio();
    }, []);

    // Handle audio playback and cleanup
    useEffect(() => {
        if (welcomeAudioUrl && audioRef.current) {
            audioRef.current.src = welcomeAudioUrl;
            audioRef.current.play().catch((error) => {
                console.error('Error auto-playing audio due to browser restrictions:', error);
                toast.error();
                setIsPlaying(false);
            });
        }

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
        };
    }, [welcomeAudioUrl]);

    // Pause audio when on Movie View page
    useEffect(() => {
        if (location.pathname.startsWith('/movie/') && audioRef.current && !audioRef.current.paused) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    }, [location.pathname]);

    // Toggle play/pause
    const toggleAudio = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play().catch((error) => {
                    console.error('Error playing audio:', error);
                    toast.error('Renah is away!');
                });
            }
            setIsPlaying(!isPlaying);
        } else if (!welcomeAudioUrl) {
            toast.error('Renah is busy now!');
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
        }
    };

    const toggleMenu = (e) => {
        e?.stopPropagation();
        setIsMenuOpen(!isMenuOpen);
    };

    const handleCreateClick = () => {
        navigate("/upload-status");
    };

    const handleMenuItemClick = (path) => {
        navigate(path);
        setIsMenuOpen(false);
    };

    // Mark all notifications as read and navigate to notifications page
    const handleNotificationsClick = async () => {
        if (!auth.currentUser) return;

        try {
            const q = query(
                collection(db, `notifications/${auth.currentUser.uid}/userNotifications`),
                where('isRead', '==', false)
            );
            const querySnapshot = await getDocs(q);

            const updatePromises = querySnapshot.docs.map((docSnapshot) =>
                updateDoc(doc(db, `notifications/${auth.currentUser.uid}/userNotifications`, docSnapshot.id), {
                    isRead: true
                })
            );

            await Promise.all(updatePromises);
        } catch (error) {
            console.error('Error marking notifications as read:', error);
            toast.error('Failed to update notifications');
        }

        navigate("/notifications");
    };

    const MenuItems = () => (
        <>
            {/* Desktop-only menu items */}
            <li 
                className={`hidden md:flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer ${location.pathname === "/" ? "text-black bg-gray-100" : "text-gray-800"}`}
                onClick={() => handleMenuItemClick("/")}
            >
                {location.pathname === "/" ? (
                    <GoHomeFill size={23} className="mr-3.5 text-black" />
                ) : (
                    <GoHome size={23} className="mr-3.5" />
                )}
                Home
            </li>
            <li 
                className={`hidden md:flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer ${location.pathname === "/genre" ? "text-black bg-gray-100" : "text-gray-800"}`}
                onClick={() => handleMenuItemClick("/genre")}
            >
                {location.pathname === "/genre" ? (
                    <TbCategoryFilled size={23} className="mr-3.5 text-black" />
                ) : (
                    <TbCategory size={23} className="mr-3.5" />
                )}
                Genre
            </li>
            <li 
                className={`hidden md:flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer ${location.pathname === "/status" ? "text-black bg-gray-100" : "text-gray-800"}`}
                onClick={() => handleMenuItemClick("/status")}
            >
                {location.pathname === "/status" ? (
                    <BsArrowUpCircleFill size={22} className="mr-4 text-black" />
                ) : (
                    <LuCircleFadingArrowUp size={22} className="mr-4" />
                )}
                Status
            </li>
            <li 
                className={`hidden md:flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer ${location.pathname === "/inbox" ? "text-black bg-gray-100" : "text-gray-800"}`}
                onClick={() => handleMenuItemClick("/messages")}
            >
                {location.pathname === "/messages" ? (
                    <AiFillMessage size={23} className="mr-3.5 text-black" />
                ) : (
                    <AiOutlineMessage size={23} className="mr-3.5" />
                )}
                Inbox
            </li>
            
            {/* Divider after desktop-only items */}
            <div className="border-t border-gray-200 my-1"></div>
            
            <li 
                className={`flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer`}
                onClick={toggleAudio}
            >
                {isPlaying ? (
                    <FaPause size={23} className="mr-4 text-pink-700" />
                ) : (
                    <FaPlay size={23} className="mr-4 text-pink-700" />
                )}
                <span className="bg-gradient-to-r from-[#FF1493] via-[#FF8C00] to-[#FFD700] bg-clip-text text-transparent">
                    Aweit AI
                </span>
               
            </li>
            
            <li 
                className={`flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer ${location.pathname === "/subscriptions" ? "text-black bg-gray-100" : "text-gray-800"}`}
                onClick={() => handleMenuItemClick("/subscriptions")}
            >
                <MdOutlinePayments 
                    size={22} 
                    className="mr-4 text-pink-700" 
                />
                <span className="bg-gradient-to-r from-[#FF1493] via-[#FF8C00] to-[#FFD700] bg-clip-text text-transparent">
                    Subscriptions
                </span>
            </li>
            
            {/* Divider after Subscriptions */}
            <div className="border-t border-gray-200 my-1"></div>
            
            <li 
                className={`flex items-center px-4 py-1 hover:bg-gray-100 cursor-pointer ${location.pathname === "/aweit-billboard" ? "text-black bg-gray-100" : "text-gray-800"}`}
                onClick={() => handleMenuItemClick("/aweit-billboard")}
            >
                Aweit
                <SiBillboard size={60} className="mr-4" />
            </li>
            <li 
                className={`flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer ${location.pathname === "/contact-us" ? "text-black bg-gray-100" : "text-gray-800"}`}
                onClick={() => handleMenuItemClick("/contact-us")}
            >
                <IoMdHelpCircleOutline size={22} className="mr-4" />
                Support
            </li>
            <li 
                className={`flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer ${location.pathname === "/about-us" ? "text-black bg-gray-100" : "text-gray-500"}`}
                onClick={() => handleMenuItemClick("/about-us")}
            >
                <GrCircleInformation size={22} className="mr-4 text-gray-500" />
                About Us
            </li>
            <li 
                className={`flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer ${location.pathname === "/philanthropy" ? "text-black bg-gray-100" : "text-gray-500"}`}
                onClick={() => handleMenuItemClick("/philanthropy")}
            >
                <LiaHandsHelpingSolid size={22} className="mr-4 text-gray-500" />
                Philanthropy
            </li>
            
            {/* Divider before Sign In (only shown when sign in options are visible) */}
            {pageState === "Sign In" && (
                <>
                    <div className="border-t border-gray-200 my-1"></div>
                    <li 
                        className={`flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer ${location.pathname === "/sign-in" ? "text-black bg-gray-100" : "text-gray-800"}`}
                        onClick={() => handleMenuItemClick("/sign-in")}
                    >
                        <span className="mr-4 w-5"></span>
                        Sign In
                    </li>
                    <li 
                        className={`flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer ${location.pathname === "/sign-up" ? "text-black bg-gray-100" : "text-gray-800"}`}
                        onClick={() => handleMenuItemClick("/sign-up")}
                    >
                        <span className="mr-4 w-5"></span>
                        Sign Up
                    </li>
                </>
            )}
        </>
    );

    return (
        <>
            {/* Header */}
            <div className={`bg-black border-b border-gray-800 shadow-sm sticky top-0 z-40 h-14 transition-transform duration-300 ${isVisible || isMenuOpen ? 'translate-y-0' : '-translate-y-full'}`}>
                <header className="flex justify-between items-center px-4 h-full">
                    {/* Left Section - Menu and Logo */}
                    <div className="flex items-center space-x-4">
                        {/* Menu Icon - Only visible on large screens */}
                        <button 
                            ref={menuButtonRef}
                            onClick={toggleMenu}
                            className="hidden md:flex p-2 rounded-full hover:bg-gray-800 transition-colors"
                        >
                            {isMenuOpen ? (
                                <FaTimes className="text-white text-xl" />
                            ) : (
                                <FaBars className="text-white text-xl" />
                            )}
                        </button>
                        
                        {/* Mobile Menu Icon */}
                        <button 
                            ref={menuButtonRef}
                            onClick={toggleMenu}
                            className="md:hidden p-2 rounded-full hover:bg-gray-800 transition-colors"
                        >
                            {isMenuOpen ? (
                                <FaTimes className="text-white text-xl" />
                            ) : (
                                <FaBars className="text-white text-xl" />
                            )}
                        </button>

                        <img 
                            src={window.innerWidth <= 768 ? MobileLogo : Logo} 
                            alt="AweitFlix logo" 
                            className="h-8 md:h-20 cursor-pointer"
                            onClick={() => navigate("/")} 
                        />
                    </div>

                    {/* Center Section - Search Bar (Desktop Only) */}
                    <div className="hidden md:flex flex-1 max-w-2xl mx-8">
                        <form onSubmit={handleSearch} className="w-full flex">
                            <input
                                type="text"
                                placeholder="Search AweitFlix Movies..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-2 rounded-l-full focus:outline-none focus:border-[#FF1493]"
                            />
                            <button 
                                type="submit"
                                className="bg-gray-700 px-5 rounded-r-full border border-l-0 border-gray-700 hover:bg-gray-600 transition-colors"
                            >
                                <FaSearch className="text-white" />
                            </button>
                        </form>
                    </div>

                    {/* Right Section - Actions and Auth */}
                    <div className="flex items-center space-x-4">
                        {/* Mobile Search Icon */}
                        <button 
                            onClick={() => navigate("/search")}
                            className="md:hidden p-2 rounded-full hover:bg-gray-800 transition-colors"
                        >
                            <FaSearch className="text-white text-xl" size={23} />
                        </button>

                        {/* Mobile Default Avatar when not signed in */}
                        {pageState === "Sign In" && (
                            <button 
                                onClick={() => navigate("/welcome")}
                                className="md:hidden flex items-center justify-center w-9 h-9 rounded-full overflow-hidden"
                            >
                                <img 
                                    src={defaultAvatar}
                                    alt="WelcomePage"
                                    className="w-full h-full object-cover"
                                />
                            </button>
                        )}

                        {/* Create Button */}
                        <button 
                            onClick={handleCreateClick}
                            className="hidden md:flex items-center space-x-2 bg-gray-800 px-4 py-2 rounded-full hover:bg-gray-700 transition-colors"
                        >
                            <FaPlus className="text-white" />
                            <span className="text-sm font-medium text-white">Create</span>
                        </button>

                        {/* Auth Buttons */}
                        {pageState === "Sign In" ? (
                            <>
                                <button 
                                    onClick={() => navigate("/sign-in")}
                                    className="hidden md:flex items-center justify-center bg-white text-black px-4 py-2 rounded-full hover:bg-gray-200 transition-colors"
                                >
                                    <span className="text-sm font-medium">Sign In</span>
                                </button>
                                <button 
                                    onClick={() => navigate("/sign-up")}
                                    className="hidden md:flex items-center justify-center text-white px-4 py-2 rounded-full shadow-lg transition duration-150 ease-in-out hover:shadow-xl bg-gradient-to-r from-[#FF1493] via-[#FF8C00] to-[#FFD700] animate-gradient-x"
                                >
                                    <span className="text-sm font-medium">Sign Up</span>
                                </button>
                            </>
                        ) : (
                            <div className="flex items-center space-x-3">
                                <button 
                                    onClick={() => navigate("/users")}
                                    className="p-2 rounded-full hover:bg-gray-800 transition-colors"
                                >
                                    <FaUsers className="text-white text-xl" size={26} />
                                </button>
                                <button 
                                    onClick={handleNotificationsClick} 
                                    className="relative p-2 rounded-full hover:bg-gray-800 transition-colors"
                                >
                                    <FaBell className="text-white text-xl" size={22} />
                                    {notificationCount > 0 && (
                                        <span className="absolute top-1 right-1 bg-red-500 text-white text-xs font-medium rounded-full px-1.5 py-0.5">
                                            {notificationCount >= 99 ? '99+' : notificationCount}
                                        </span>
                                    )}
                                </button>
                                <button 
                                    onClick={() => navigate("/profile")}
                                    className="flex items-center justify-center w-9 h-9 rounded-full overflow-hidden"
                                >
                                    <img 
                                        src={profileImage}
                                        alt="Profile"
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            </div>
                        )}
                    </div>
                </header>
            </div>

            {/* Desktop Menu Dropdown */}
            {isMenuOpen && (
                <>
                    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-45"></div>
                    <div 
                        ref={menuRef}
                        className="hidden md:block fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50 overflow-y-auto"
                        style={{ transition: 'opacity 0.3s ease', opacity: isMenuOpen ? 1 : 0, pointerEvents: isMenuOpen ? 'auto' : 'none' }}
                    >
                        <div className="flex items-center p-4 border-b border-gray-200">
                            <button 
                                onClick={toggleMenu}
                                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                            >
                                <FaTimes className="text-black text-xl" />
                            </button>
                            <span 
                                className="text-black text-lg font-medium cursor-pointer ml-2"
                                onClick={toggleMenu}
                            >
                                Close
                            </span>
                        </div>
                        <ul className="flex flex-col py-2">
                            <MenuItems />
                        </ul>
                    </div>
                </>
            )}

            {/* Mobile Menu Dropdown */}
            {isMenuOpen && (
                <>
                    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-45 md:hidden"></div>
                    <div 
                        ref={menuRef}
                        className="md:hidden fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50 overflow-y-auto"
                        style={{ transition: 'opacity 0.3s ease', opacity: isMenuOpen ? 1 : 0, pointerEvents: isMenuOpen ? 'auto' : 'none' }}
                    >
                        <div className="flex items-center p-4 border-b border-gray-200">
                            <button 
                                onClick={toggleMenu}
                                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                            >
                                <FaTimes className="text-black text-xl" />
                            </button>
                            <span 
                                className="text-black text-lg font-medium cursor-pointer ml-2"
                                onClick={toggleMenu}
                            >
                                Close
                            </span>
                        </div>
                        <ul className="flex flex-col py-2">
                            <MenuItems />
                        </ul>
                    </div>
                </>
            )}
            <audio ref={audioRef} />
        </>
    );
}